// /reports/peak-hours — Peak Hours (PAGES.md §3.47)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { HeatmapChart } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

interface HourlyRow {
  id: string;
  hour: number;
  sales: number;
  bills: number;
}

function makeColumns(currency: CurrencyCode): ColumnDef<HourlyRow>[] {
  return [
    { key: 'hour', header: 'Hour', render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{String(row.hour).padStart(2, '0')}:00 – {String(row.hour + 1).padStart(2, '0')}:00</span> },
    { key: 'bills', header: 'Bills', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.bills.toLocaleString('en-IN')}</span> },
    { key: 'sales', header: 'Sales', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.sales, currency)}</span> },
  ];
}

export function PeakHoursReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'peak-hours',
    filter,
    queryFn: (f) => reportsApi.getPeakHours(f),
  });

  const hourly: HourlyRow[] = useMemo(
    () => (data?.hourly ?? []).map((h) => ({ id: String(h.hour), ...h })),
    [data],
  );

  const heatmapCells = useMemo(
    () =>
      (data?.cells ?? []).map((c) => ({
        row: c.day,
        col: `${String(c.hour).padStart(2, '0')}h`,
        value: c.sales / subunit,
      })),
    [data, subunit],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `peak-hours-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Hour', get: (r: HourlyRow) => r.hour },
          { header: 'Bills', get: (r: HourlyRow) => r.bills },
          { header: `Sales (${symbol})`, get: (r: HourlyRow) => (r.sales / subunit).toFixed(csvDecimals) },
        ],
        hourly,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Peak Hours', `Period ${filter.range.from} → ${filter.range.to}`);
    }
  };

  return (
    <ReportPageShell
      title="Peak Hours"
      subtitle="Hour-of-day and day-of-week heatmap — optimise staffing and stock levels."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Peak Hours' }]}
      accent="indigo-violet"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!hourly.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={0} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          {heatmapCells.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <HeatmapChart data={heatmapCells} title={`Sales by Day × Hour (${symbol})`} rowLabel="Day" colLabel="Hour" />
            </div>
          ) : null}
          <ReportTable title="Hourly Breakdown" columns={columns} data={hourly} emptyTitle="Not enough data for peak hour analysis" totalCount={hourly.length} />
        </>
      )}
    </ReportPageShell>
  );
}
